/**
 * "Plays once per seat" — the first-run rail's completion mark.
 *
 * TWO marks, and the server's wins.
 *
 * The browser mark is the local one: `chai-demo-first-run-seen:<sub>:<user>`,
 * matching the house pattern its neighbours already use (`chai-demo-tour-seen`
 * in DemoTour, `chai-demo-welcome-seen` in DemoWelcomeBanner) — keyed by demo
 * AND seat, so two personas sharing one browser during a live pitch each get
 * their own first run. It cannot travel: a prospect who opens the demo link on
 * their laptop and then their phone would get the 90-second arc twice.
 *
 * The SERVER mark closes that. On dismissal the rail reports one more
 * `first_run` beacon with `state: "done"` through the engagement channel it is
 * already using for every other transition, and `/me` answers
 * `demo.first_run_seen: true` for that seat from then on. No new preferences
 * store stands behind it: the completion fact and the ops-funnel fact are the
 * same event, on a pipe that already exists (the earlier TODO here wanted a
 * per-seat settings bag, and widening `user.notification_prefs` — a typed
 * toggle set — into one would have been more API surface than a boolean is
 * worth). The honest weakness: a dropped beacon costs a replay on a SECOND
 * machine, never a wrong app state, because the local mark still covers the
 * browser the prospect actually used.
 *
 * The 3-attempt resume cap stays purely client-side — it is a property of
 * this browser's patience, not of the seat.
 *
 * Storage can throw outright (Safari private mode, blocked site data). Every
 * accessor here swallows that and answers "already seen", because the failure
 * mode of a rail that can never record its completion is a rail that replays
 * on every navigation — far worse than one that never opens.
 */

// Relative, not the `@/` alias, so this module stays compilable on its own —
// the house test convention for the demo chrome's pure modules runs them
// straight through tsc (see ./firstRun.test.ts, ./seen.test.ts).
import { postDemoEvent } from "../../../lib/demoEvents";

/** Window event the rail fires once it dismisses. Informational: nothing in
 *  the app subscribes today — the guided tour deliberately does NOT (its
 *  auto-start stands down for the whole session the rail played in, see
 *  DemoTour.tsx), and the beacon stays for any surface that wants to react
 *  to the arc ending without re-reading localStorage. */
export const FIRST_RUN_DONE_EVENT = "chaidojo:first-run-done";

/** Auto-open attempts before the rail gives up, mirroring DemoTour's cap. A
 *  mid-arc refresh should resume the pitch; an ignored rail shouldn't nag. */
export const FIRST_RUN_MAX_ATTEMPTS = 3;

/**
 * Why the rail is on screen.
 *
 *  - `"auto"` — the seat is owed its first run. Counts an attempt, writes both
 *    seen-marks on dismissal, beacons every transition.
 *  - `"replay"` — the prospect asked for it again ("Replay the intro" in the
 *    sidebar). Writes NOTHING and reports nothing.
 *
 * The replay exists because of a second verdict from the same walkthrough:
 * "somebody who skimmed the first flow can never take that path again." A
 * replay that recorded itself would defeat its own purpose in both directions —
 * it would burn the attempt cap of a seat still owed the real thing, and it
 * would end the arc early on the prospect's other machine.
 *
 * Every writer below takes the mode and no-ops on `"replay"`, so the rule lives
 * in one testable place rather than being re-argued at each call site.
 */
export type FirstRunMode = "auto" | "replay";

const seenKey = (subdomain: string, userKey: string) =>
  `chai-demo-first-run-seen:${subdomain || "demo"}:${userKey || "anon"}`;

const sessionKey = (subdomain: string, userKey: string) =>
  `chai-demo-first-run-session:${subdomain || "demo"}:${userKey || "anon"}`;

/** True when THIS browser session showed the rail. The guided tour reads it
 *  to keep its auto-start down for the whole session the arc played in — an
 *  in-memory flag cannot carry that, because the tour chrome unmounts and
 *  remounts across route-level data refetches (filmed: the tour auto-opened
 *  over the deck artifact right after the rail closed, because the remount
 *  re-read only the persistent seen-mark). sessionStorage on purpose: it
 *  survives remounts and navigation but dies with the tab, so the NEXT
 *  login's tour auto-start guarantee is untouched. Unreadable storage means
 *  "no" — in that world the rail never opened anyway (isFirstRunSeen answers
 *  true), so the tour keeping its normal auto-start is the right outcome. */
export function firstRunPlayedThisSession(subdomain: string, userKey: string): boolean {
  try {
    return sessionStorage.getItem(sessionKey(subdomain, userKey)) !== null;
  } catch {
    return false;
  }
}

function markSessionPlayed(subdomain: string, userKey: string): void {
  try {
    sessionStorage.setItem(sessionKey(subdomain, userKey), "1");
  } catch {
    /* covered above: no storage means the rail never opened */
  }
}

/** The `state` the terminal beacon carries. The server reads exactly this
 *  value as the seat's completion mark (services/demo_scope.first_run_seen),
 *  so it is a constant on both sides, not a string typed twice. */
export const FIRST_RUN_DONE_STATE = "done";

/**
 * Does this mount report its transitions to the engagement channel?
 *
 * Only `"auto"` does, and the reason is the server's context whitelist. POST
 * /demo/events keeps `path` / `workspace_id` / `phase` / `ext` / `state` and
 * numeric `ms` / `value`, and DROPS everything else. So a `replay: true` tag on
 * a replayed beacon would never arrive: the ops funnel would receive a stream
 * of `first_run` transitions indistinguishable from a fresh seat's, and the
 * one number the funnel exists to report — how many prospects finished the arc
 * — would count the same person twice.
 *
 * Given the choice the brief allows (tag the beacons, or omit them), a replay
 * that cannot be tagged truthfully is a replay that stays quiet. Re-enabling
 * them is a one-line change here the day `replay` joins the server whitelist.
 */
export function firstRunReports(mode: FirstRunMode): boolean {
  return mode === "auto";
}

/** True once this seat has finished (or skipped past) the arc, or when the
 *  attempt cap is spent, or when storage is unavailable.
 *
 *  `serverSeen` is `me.demo.first_run_seen` — trusted when it is true (this
 *  seat completed the arc, on whatever machine), ignored when it is false or
 *  absent, which is exactly the pre-server behaviour. It is deliberately
 *  one-way: the server can only ever END the arc early, never restart one the
 *  browser has already marked done. */
export function isFirstRunSeen(
  subdomain: string,
  userKey: string,
  serverSeen?: boolean,
): boolean {
  if (serverSeen === true) return true;
  const key = seenKey(subdomain, userKey);
  try {
    if (localStorage.getItem(key) !== null) return true;
    return Number(localStorage.getItem(`${key}:attempts`) || "0") >= FIRST_RUN_MAX_ATTEMPTS;
  } catch {
    return true; // no storage -> never auto-open (avoid a replay loop)
  }
}

/** Count one auto-open. Called when the rail actually mounts and shows —
 *  which is also the moment this browser session becomes the rail's.
 *
 *  A `"replay"` mount counts nothing and claims nothing: it must not spend the
 *  cap of a seat that is still owed its real first run, and it must not
 *  suppress the guided tour's auto-start on a session where the arc never
 *  actually played. */
export function noteFirstRunAttempt(
  subdomain: string,
  userKey: string,
  mode: FirstRunMode = "auto",
): void {
  if (mode === "replay") return;
  markSessionPlayed(subdomain, userKey);
  const key = `${seenKey(subdomain, userKey)}:attempts`;
  try {
    localStorage.setItem(key, String(Number(localStorage.getItem(key) || "0") + 1));
  } catch {
    /* ignore — isFirstRunSeen already refuses to open without storage */
  }
}

/** Mark the arc done for this seat — locally AND on the server. The tour's
 *  auto-start stays down for the rest of this session (see
 *  firstRunPlayedThisSession) and resumes on the next login.
 *
 *  Both marks are written here rather than in the rail, so the rail's state
 *  machine stays a state machine: dismissal is one call, and where the
 *  completion is recorded is this module's business. The beacon is
 *  fire-and-forget like every other one; a failure costs a replay on another
 *  machine and nothing else.
 *
 *  In `"replay"` mode this is a no-op in full: no local mark, no session claim,
 *  no completion beacon. Closing a replay leaves the seat exactly as it was —
 *  including a seat that has never seen the arc, which is still owed it. */
export function markFirstRunSeen(
  subdomain: string,
  userKey: string,
  mode: FirstRunMode = "auto",
): void {
  if (mode === "replay") return;
  markSessionPlayed(subdomain, userKey);
  try {
    localStorage.setItem(seenKey(subdomain, userKey), new Date().toISOString());
  } catch {
    /* dismissal still holds for this page's lifetime */
  }
  postDemoEvent("first_run", { state: FIRST_RUN_DONE_STATE });
  try {
    window.dispatchEvent(new CustomEvent(FIRST_RUN_DONE_EVENT));
  } catch {
    /* non-DOM context (tests) — nothing listening anyway */
  }
}
